import {
    TagTree,
    TagRecord,
    ProjectExtended,
    OrganizationPermissions,
    TagExtended,
    organizationPermissionsSchema,
} from "@nova/schemas"
import { arrToMap, isDefined } from "nodash"
import { Permissions } from "@nova/constants"

// find path to target
export function findTagTreePath(
    tree: TagTree,
    targetId: string,
    path: string[] = []
): string[] | null {
    for (const [id, value] of Object.entries(tree)) {
        const currentPath = [...path, id]

        // If the current key matches the targetId, return the path
        if (id === targetId) {
            return currentPath
        }

        // If the value is an object, recurse into it
        if (typeof value === "object") {
            const result = findTagTreePath(value, targetId, currentPath)
            if (result) {
                return result
            }
        }
    }

    return null
}

const getByPath = (tree: TagTree, path: string[]) => path.reduce((node, key) => node[key]!, tree)

/**
 * Returns a new tree with the node at `id` moved to be a child of `newParentId`.
 * If `newParentId` is not specified, the node is moved to the root.
 */
export function moveTagInTree(tree: TagTree, id: string, newParentId?: string) {
    const newTree = JSON.parse(JSON.stringify(tree)) as TagTree

    const sourcePath = findTagTreePath(newTree, id)
    let treeToMove = {} as TagTree

    if (sourcePath) {
        const sourceParentPath = sourcePath.slice(0, -1)
        const oldParentTree =
            sourceParentPath.length > 0 ? getByPath(newTree, sourceParentPath) : newTree
        treeToMove = oldParentTree[id] ?? {}
        delete oldParentTree[id]
    }

    const newParentPath = newParentId ? findTagTreePath(newTree, newParentId) : undefined
    if (newParentId && !newParentPath)
        throw new Error(`Parent tag ${newParentId} not found in tree`)
    const newParentTree = newParentPath ? getByPath(newTree, newParentPath) : newTree
    newParentTree[id] = treeToMove

    return newTree
}

export function deleteTagInTree(tree: TagTree, id: string) {
    try {
        const newTree = JSON.parse(JSON.stringify(tree)) as TagTree

        const path = findTagTreePath(newTree, id)
        if (!path) return

        const subTree = getByPath(newTree, path.slice(0, -1))
        if (!subTree[path.at(-1) || ""]) return

        delete subTree[path.at(-1)!]

        return newTree
    } catch {
        // will throw if bad data, in which case delete fails and return undefined
        return
    }
}

// given tree, hydrate tags with subTags and projects
export const traverseTagTree = (
    tree: TagTree,
    tags: TagRecord[],
    projects: ProjectExtended[],
    permissions: OrganizationPermissions
): TagExtended[] => {
    const tagMap = arrToMap(tags, "id")
    const permissionMap = arrToMap(permissions.subRoles, "id")

    const getPermission = (id: string) => {
        return permissionMap[id]?.permission || 0
    }

    const traverse = (tree: TagTree, permissionLevel: number): TagExtended[] => {
        return Object.keys(tree)
            .map((tagId) => {
                const effectivePermission = permissionLevel | getPermission(tagId)
                const tag = tagMap[tagId]
                // Handle case when folder tag has been deleted, but not cleared from member tagTree
                if (!tag) return

                const folderProjects = projects.filter(
                    (project) =>
                        project.tagId === tagId && effectivePermission | getPermission(project.id)
                )

                const childTree = tree[tagId]
                const childFolders = childTree ? traverse(childTree, effectivePermission) : []

                if (!effectivePermission && !childFolders.length && !folderProjects.length) return
                return {
                    id: tag.id,
                    orgId: tag.rootId,
                    name: tag.fullName,
                    createdAt: tag.createdAt,
                    updatedAt: tag.updatedAt,
                    createdBy: tag.createdBy,
                    updatedBy: tag.updatedBy,
                    subTags: childFolders,
                    projects: folderProjects,
                }
            })
            .filter(isDefined)
    }

    return traverse(tree, permissions.workspaceAccess || 0)
}

export const retrievePermissionsFromTagTree = (
    permissions?: OrganizationPermissions,
    tagTree?: TagTree,
    project?: { tagId?: string; id: string },
    entityId?: string
) => {
    tagTree = tagTree || {}
    const validatedPermissions = organizationPermissionsSchema.safeParse(permissions).data
    if (!validatedPermissions) return Permissions.None

    let permissionLevel = Permissions.None as Permissions
    //search for specifically the entity we're asking for
    permissionLevel =
        validatedPermissions.subRoles.find((role) => role.id === entityId)?.permission ||
        Permissions.None

    if (permissionLevel) return permissionLevel

    const tagId = project?.tagId
    let path: string[] | null = []
    if (project && project.id === entityId) {
        path = findTagTreePath(tagTree, tagId || "", [])
    } else if (entityId) {
        path = findTagTreePath(tagTree, entityId, [])
        path?.pop()
    }

    // otherwise, search up the path to find the most immediate entity that has access
    for (const id of path?.reverse() || []) {
        permissionLevel =
            validatedPermissions.subRoles.find((role) => role.id === id)?.permission ||
            Permissions.None
        if (permissionLevel) return permissionLevel
    }
    return validatedPermissions.workspaceAccess
}

export const flattenSubTags = (tags?: TagExtended[]) => {
    const flatten = (tags: TagExtended[]): TagExtended[] =>
        tags.flatMap((tag) => [tag, ...flatten(tag.subTags)])
    return flatten(tags || [])
}
